import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft,
  ChevronRight,
  GripVertical,
  X,
  RotateCcw,
  BookOpen,
  ScanLine,
  Eye,
  Highlighter,
  HelpCircle,
  Map,
  Gamepad2,
  Globe,
  ClipboardList,
  Pencil,
  Search,
  RefreshCw,
  Users,
  Clock,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { WEEK_DAYS } from '@/shared/types/common'
import type { WeekDay } from '@/shared/types/common'
import type {
  PlanningDataDto,
  PlanningRowDto,
  PlanningCellDto,
  ProgrammePlanningCellItemDto,
  ActivityCellItemDto,
  LangueDto,
} from '@/shared/types/planning'
import {
  usePlanning,
  useUpdateProgrammePlanning,
  useDeactivateProgrammePlanning,
  useReactivateProgrammePlanning,
  useUpdateLangueStart,
} from '../hooks/use-planning'

// ── Constants ───────────────────────────────────────────

const DAY_LABELS: Record<WeekDay, string> = {
  LUNDI: 'Lundi',
  MARDI: 'Mardi',
  MERCREDI: 'Mercredi',
  JEUDI: 'Jeudi',
  VENDREDI: 'Vendredi',
  SAMEDI: 'Samedi',
  DIMANCHE: 'Dimanche',
}

const DAY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  LUNDI: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  MARDI: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  MERCREDI: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  JEUDI: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  VENDREDI: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  SAMEDI: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  DIMANCHE: { bg: 'bg-neutral-50', text: 'text-neutral-500', border: 'border-neutral-200' },
}

// Icon mapping: backend icon string → Lucide component
const ICON_MAP: Record<string, typeof BookOpen> = {
  BookOpen,
  ScanLine,
  Eye,
  Search,
  RefreshCw,
  Highlighter,
  HelpCircle,
  Map,
  Gamepad2,
  Globe,
  ClipboardList,
  Pencil,
  Users,
}

// Fallback icon mapping by actionId (matches old frontend ACTION_ROWS)
const ACTION_ICON_MAP: Record<number, typeof BookOpen> = {
  1: Search,      // Scan
  2: RefreshCw,   // Relire
  3: Highlighter, // Souligner
  4: Users,       // CQQCOQP
  5: Map,         // Map
}

// Langue labels: 4-day vocabulary learning sequence
// Split: deuxiemeLot = min(5, floor(nbMots/2)), premierLot = nbMots - deuxiemeLot
// Ex: 5→3+2, 10→5+5, 15→10+5, 20→15+5
function getLangueLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  dayOffset: number,
  nbMots: number,
): string {
  const deuxiemeLot = Math.min(5, Math.floor(nbMots / 2))
  const premierLot = nbMots - deuxiemeLot

  switch (dayOffset) {
    case 0:
      return t('student.planning.table.language.learnWords', { count: premierLot })
    case 1:
      return t('student.planning.table.language.reviewWords', { count: premierLot })
    case 2:
      return t('student.planning.table.language.learnMoreWords', { count: deuxiemeLot })
    case 3:
      return t('student.planning.table.language.reviewAllWords', { count: nbMots })
    case 5:
      return t('student.planning.table.language.reviewAllWords', { count: nbMots })
    default:
      return ''
  }
}

// ── Helper: resolve icon for a row ──────────────────────

function getRowIcon(row: PlanningRowDto): typeof BookOpen | null {
  // First try backend icon string
  if (row.icon && ICON_MAP[row.icon]) return ICON_MAP[row.icon]
  // Fallback by actionId
  if (row.actionId && ACTION_ICON_MAP[row.actionId]) return ACTION_ICON_MAP[row.actionId]
  // Fallback by category keyword
  if (row.category.includes('revisions')) return BookOpen
  if (row.category.includes('scanner')) return Eye
  if (row.category.includes('devoirs')) return ClipboardList
  if (row.category.includes('loisirs')) return Gamepad2
  if (row.category.includes('culture')) return Pencil
  if (row.category.includes('langue')) return Globe
  return null
}

// ── Helper: classify rows into sections ─────────────────

type SectionKey = 'revisions' | 'programme' | 'langues' | 'activites'

function getProgrammeRowOrder(row: PlanningRowDto): number {
  const cat = row.category.toLowerCase()
  if (cat.includes('scanner')) return 0
  if (row.actionId === 1) return 1   // Scan
  if (row.actionId === 2) return 10  // Relire
  if (row.actionId === 3) return 20  // Surligner
  if (row.actionId === 4) return 30  // CQQCOQP
  if (row.actionId === 5) return 80  // Carte mentale
  if (cat.includes('devoir')) return 99 // Devoirs (toujours en dernier)
  return 50
}

interface Section {
  key: SectionKey
  titleKey: string
  titleFallback: string
  accent: string
  rows: PlanningRowDto[]
}

function classifyRows(
  rows: PlanningRowDto[],
  langues: LangueDto[],
): Section[] {
  const revisionsRows: PlanningRowDto[] = []
  const programmeRows: PlanningRowDto[] = []
  const activityRows: PlanningRowDto[] = []
  const langueRows: PlanningRowDto[] = []

  for (const row of rows) {
    const cat = row.category.toLowerCase()
    if (cat.includes('langue')) {
      langueRows.push(row)
    } else if (cat.includes('revis')) {
      revisionsRows.push(row)
    } else if (row.actionId || cat.includes('scanner') || cat.includes('devoir')) {
      programmeRows.push(row)
    } else {
      activityRows.push(row)
    }
  }

  // Sort programme rows: scan, relire, surligner, ..., carte mentale, devoirs (last)
  programmeRows.sort((a, b) => getProgrammeRowOrder(a) - getProgrammeRowOrder(b))

  // If backend didn't provide langue rows but we have langues, create virtual rows
  if (langueRows.length === 0 && langues.length > 0) {
    langues.forEach((langue, idx) => {
      langueRows.push({
        id: `langue-${idx + 1}`,
        title: langue.matiereNom,
        category: `langue-${idx + 1}`,
        backgroundColor: idx % 2 === 0 ? '#fff8e1' : '#fff3e0',
        icon: 'Globe',
        activityType: null,
        actionId: null,
        dureeMinutes: null,
        matiereId: langue.matiereId,
        matiereNom: langue.matiereNom,
        matiereCouleur: langue.matiereCouleur,
      })
    })
  }

  return [
    {
      key: 'revisions',
      titleKey: 'student.planning.sections.revisions',
      titleFallback: 'Révisions',
      accent: 'text-yellow-700',
      rows: revisionsRows,
    },
    {
      key: 'programme',
      titleKey: 'student.planning.sections.programme',
      titleFallback: "Programme d'étude",
      accent: 'text-primary',
      rows: programmeRows,
    },
    {
      key: 'langues',
      titleKey: 'student.planning.sections.languages',
      titleFallback: 'Langues',
      accent: 'text-amber-700',
      rows: langueRows,
    },
    {
      key: 'activites',
      titleKey: 'student.planning.sections.activities',
      titleFallback: 'Activités personnelles',
      accent: 'text-emerald-700',
      rows: activityRows,
    },
  ]
}

// ── Sub-component: Matière chip (programme planning) ────

function MatiereChip({
  item,
  draggable,
  onDeactivate,
  onReactivate,
  onDragStart,
  onDragEnd,
}: {
  item: ProgrammePlanningCellItemDto
  draggable: boolean
  onDeactivate?: () => void
  onReactivate?: () => void
  onDragStart?: (e: React.DragEvent) => void
  onDragEnd?: (e: React.DragEvent) => void
}) {
  const c = item.matiereCouleur || '#79305D'

  if (!item.actif) {
    return (
      <span className="group inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-400 opacity-60">
        {item.matiereNom}
        {onReactivate && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onReactivate()
            }}
            className="ml-0.5 opacity-0 transition-opacity group-hover:opacity-100"
            title="Réactiver"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        )}
      </span>
    )
  }

  return (
    <span
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      className={cn(
        'group inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm transition-all hover:shadow-md',
        draggable && 'cursor-grab active:cursor-grabbing',
      )}
      style={{
        backgroundColor: c + '18',
        color: c,
        borderColor: c + '40',
      }}
    >
      {draggable && <GripVertical className="h-3 w-3 opacity-40" />}
      {item.matiereNom}
      {onDeactivate && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDeactivate()
          }}
          className="ml-0.5 opacity-0 transition-opacity group-hover:opacity-100"
          title="Désactiver"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </span>
  )
}

// ── Programme row label colors (by actionId) ────────────
// Each action row in "Programme d'étude" gets a distinct label color

const PROGRAMME_ROW_LABEL_COLORS: Record<number, { bg: string; badge: string }> = {
  1: { bg: '#ffebee60', badge: '#ffebee' },   // Scan — rose clair
  2: { bg: '#e3f2fd60', badge: '#e3f2fd' },   // Relire — bleu clair
  3: { bg: '#fff3e060', badge: '#fff3e0' },   // Souligner — orange clair
  4: { bg: '#f3e5f560', badge: '#f3e5f5' },   // CQQCOQP — violet clair
  5: { bg: '#e8f5e860', badge: '#e8f5e8' },   // Map — vert clair
}

// Scanner row (no actionId) gets its own color
const SCANNER_LABEL_COLOR = { bg: '#fff8e160', badge: '#fff8e1' } // ambre clair
const DEVOIRS_LABEL_COLOR = { bg: '#fce4ec60', badge: '#fce4ec' } // rose clair

function getProgrammeRowLabelColors(row: PlanningRowDto): { bg: string; badge: string } | null {
  if (row.actionId && PROGRAMME_ROW_LABEL_COLORS[row.actionId]) {
    return PROGRAMME_ROW_LABEL_COLORS[row.actionId]
  }
  if (row.category.toLowerCase().includes('scanner')) {
    return SCANNER_LABEL_COLOR
  }
  if (row.category.toLowerCase().includes('devoir')) {
    return DEVOIRS_LABEL_COLOR
  }
  return null
}

// ── Activity row color mapping ──────────────────────────

const ACTIVITY_ROW_COLORS: Record<string, { border: string; bg: string; text: string }> = {
  revisions: { border: 'border-l-yellow-500', bg: 'bg-yellow-50/80', text: 'text-yellow-800' },
  devoirs: { border: 'border-l-pink-500', bg: 'bg-pink-50/80', text: 'text-pink-800' },
  loisirs: { border: 'border-l-sky-500', bg: 'bg-sky-50/80', text: 'text-sky-800' },
  'culture-generale': { border: 'border-l-emerald-500', bg: 'bg-emerald-50/80', text: 'text-emerald-800' },
}

const DEFAULT_ACTIVITY_COLORS = {
  border: 'border-l-violet-500',
  bg: 'bg-violet-50/80',
  text: 'text-violet-800',
}

// ── Sub-component: Activity cell item ───────────────────

function ActivityItem({
  activity,
  rowCategory,
}: {
  activity: ActivityCellItemDto
  rowCategory: string
}) {
  const colors = ACTIVITY_ROW_COLORS[rowCategory] ?? DEFAULT_ACTIVITY_COLORS

  return (
    <div
      className={cn(
        'w-full rounded-md border-l-2 px-1.5 py-1 transition-shadow hover:shadow-sm',
        colors.border,
        colors.bg,
      )}
    >
      <span className={cn('block text-[10px] font-semibold leading-tight', colors.text)}>
        {activity.title}
      </span>
      <span className="flex items-center gap-0.5 text-[9px] text-neutral-400">
        <Clock className="h-2 w-2" />
        {activity.startTime}-{activity.endTime}
      </span>
    </div>
  )
}

// ── Sub-component: Langue cell ──────────────────────────

function LangueCell({
  langue,
  dayIndex,
  nbMots,
  t,
}: {
  langue: LangueDto
  dayIndex: number
  nbMots: number
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const startIdx = langue.debutLundi ? 0 : 1
  const dayOffset = dayIndex - startIdx
  const isVisible = (dayOffset >= 0 && dayOffset < 4) || dayOffset === 5

  if (!isVisible) return null

  const label = getLangueLabel(t, dayOffset, nbMots)
  const c = langue.matiereCouleur || '#F59E0B'

  return (
    <span
      className="rounded-md px-1.5 py-0.5 text-[9px] font-medium leading-tight"
      style={{
        backgroundColor: c + '12',
        color: c,
      }}
    >
      {label}
    </span>
  )
}

// ── Main component ──────────────────────────────────────

interface PlanningTabProps {
  etudiantId: number
}

export function PlanningTab({ etudiantId }: PlanningTabProps) {
  const { t } = useTranslation()
  const { data: planningComplete, isLoading, error } = usePlanning(etudiantId)
  const updatePlanning = useUpdateProgrammePlanning(etudiantId)
  const deactivatePlanning = useDeactivateProgrammePlanning(etudiantId)
  const reactivatePlanning = useReactivateProgrammePlanning(etudiantId)
  const updateLangueStart = useUpdateLangueStart(etudiantId)

  // Navigation between plannings
  const [planningIndex, setPlanningIndex] = useState(0)

  // Collapsible sections
  const [expanded, setExpanded] = useState<Set<SectionKey>>(
    new Set(['revisions', 'programme', 'langues', 'activites']),
  )

  // Drag state
  const [dragOverCell, setDragOverCell] = useState<string | null>(null)

  const toggle = (key: SectionKey) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // ── Drag and drop handlers ──────────────────────────

  const handleDragStart = useCallback(
    (e: React.DragEvent, item: ProgrammePlanningCellItemDto, row: PlanningRowDto) => {
      if (!item.actif) {
        e.preventDefault()
        return
      }
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData(
        'application/json',
        JSON.stringify({
          id: item.id,
          actionId: item.actionId,
          rowId: row.id,
          jourDeBase: item.jourDeBase,
          jourDeclenchement: item.jourDeclenchement,
        }),
      )
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.style.opacity = '0.4'
        e.currentTarget.dataset.dragging = 'true'
        e.currentTarget.dataset.actionId = String(item.actionId)
      }
    },
    [],
  )

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1'
      delete e.currentTarget.dataset.dragging
      delete e.currentTarget.dataset.actionId
    }
    setDragOverCell(null)
  }, [])

  const handleDragOver = useCallback(
    (e: React.DragEvent, cellKey: string, row: PlanningRowDto) => {
      // Read the dragged actionId to reject cross-row drops visually
      try {
        // dataTransfer.getData is not available during dragover in some browsers,
        // but we stored actionId on the drag element via a data attribute
        const draggedActionId = (document.querySelector('[data-dragging="true"]') as HTMLElement)
          ?.dataset.actionId
        if (draggedActionId && row.actionId && Number(draggedActionId) !== row.actionId) {
          e.dataTransfer.dropEffect = 'none'
          return
        }
      } catch {
        // Fallback: allow drop (validation will happen in handleDrop)
      }
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDragOverCell(cellKey)
    },
    [],
  )

  const handleDragLeave = useCallback(() => {
    setDragOverCell(null)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent, targetDay: WeekDay, row: PlanningRowDto) => {
      e.preventDefault()
      setDragOverCell(null)

      if (!row.actionId) return

      try {
        const dataStr = e.dataTransfer.getData('application/json')
        if (!dataStr) return

        const dragged = JSON.parse(dataStr) as {
          id: number
          actionId: number
          rowId: string
          jourDeBase: WeekDay | null
          jourDeclenchement: number
        }

        // Only allow drops within the same action row
        if (dragged.actionId !== row.actionId) return

        // jourDeclenchement is a relative offset from jourDeBase.
        // Backend formula: jourReel = ((jourDeBase.dayOrder + jourDeclenchement - 1) % 7) + 1
        // We need: newJourDeclenchement such that jourReel == targetDay
        // => newJourDeclenchement = (targetDayIndex - jourDeBaseIndex + 7) % 7
        const targetDayIndex = WEEK_DAYS.indexOf(targetDay)
        const jourDeBaseIndex = dragged.jourDeBase ? WEEK_DAYS.indexOf(dragged.jourDeBase) : 0
        const newJourDeclenchement = (targetDayIndex - jourDeBaseIndex + 7) % 7

        if (newJourDeclenchement === dragged.jourDeclenchement) return // no change

        updatePlanning.mutate({ id: dragged.id, jourDeclenchement: newJourDeclenchement })
      } catch {
        console.error('Erreur lors du déplacement de la matière')
      }
    },
    [updatePlanning],
  )

  // ── Loading / Error states ──────────────────────────

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {t('student.planning.errorLoadingSchedule', 'Erreur lors du chargement du planning')}
      </div>
    )
  }

  if (!planningComplete || planningComplete.plannings.length === 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        {t(
          'student.planning.noScheduleFound',
          'Aucun emploi du temps trouvé pour cet étudiant.',
        )}
      </div>
    )
  }

  // Current planning
  const currentPlanning: PlanningDataDto =
    planningComplete.plannings[planningIndex] ?? planningComplete.plannings[0]
  const nbMots = planningComplete.nbMotsLangue || 10
  const sections = classifyRows(currentPlanning.rows, currentPlanning.langues)

  // Helper to get cell data
  const getCell = (rowId: string, day: WeekDay): PlanningCellDto | null => {
    return currentPlanning.cells?.[rowId]?.[day] ?? null
  }

  // Find langue DTO for a row
  const getLangueForRow = (row: PlanningRowDto): LangueDto | null => {
    if (!row.matiereId) return null
    return currentPlanning.langues.find((l) => l.matiereId === row.matiereId) ?? null
  }

  // ── Render ──────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Planning navigation (if multiple) */}
      {planningComplete.plannings.length > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setPlanningIndex(Math.max(0, planningIndex - 1))}
            disabled={planningIndex === 0}
            className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h3 className="text-sm font-bold text-neutral-800">
            {currentPlanning.emploiDuTempsTitre ||
              t('student.planning.defaultTitle', { index: planningIndex + 1 })}
            {currentPlanning.semaine !== 'AUCUNE' && (
              <span className="ml-2 text-xs font-normal text-neutral-500">
                — Semaine {currentPlanning.semaine}
              </span>
            )}
          </h3>
          <button
            onClick={() =>
              setPlanningIndex(
                Math.min(planningComplete.plannings.length - 1, planningIndex + 1),
              )
            }
            disabled={planningIndex === planningComplete.plannings.length - 1}
            className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Single planning: just show title */}
      {planningComplete.plannings.length === 1 && (
        <div className="text-center">
          <h3 className="text-sm font-bold text-neutral-800">
            {currentPlanning.emploiDuTempsTitre}
            {currentPlanning.semaine !== 'AUCUNE' && (
              <span className="ml-2 text-xs font-normal text-neutral-500">
                — Semaine {currentPlanning.semaine}
              </span>
            )}
          </h3>
        </div>
      )}

      {/* Day header — always visible */}
      <div className="flex gap-1 pl-48">
        {WEEK_DAYS.map((day) => {
          const colors = DAY_COLORS[day]
          return (
            <div
              key={day}
              className={cn(
                'flex-1 rounded-xl py-3 text-center text-xs font-bold uppercase tracking-wider shadow-sm border',
                colors.bg,
                colors.text,
                colors.border,
              )}
            >
              {DAY_LABELS[day]}
            </div>
          )
        })}
      </div>

      {/* Sections */}
      {sections.map(({ key, titleFallback, accent, rows }) => {
        if (rows.length === 0) return null

        return (
          <div
            key={key}
            className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm"
          >
            {/* Section header */}
            <button
              type="button"
              onClick={() => toggle(key)}
              className="flex w-full items-center justify-between px-4 py-2.5 transition-colors hover:bg-neutral-50"
            >
              <span className={cn('text-sm font-bold', accent)}>
                {t(`student.planning.sections.${key}`, titleFallback)}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-neutral-400">
                  {rows.length} ligne{rows.length > 1 ? 's' : ''}
                </span>
                {expanded.has(key) ? (
                  <ChevronLeft className="h-4 w-4 rotate-90 text-neutral-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 -rotate-90 text-neutral-400" />
                )}
              </div>
            </button>

            {expanded.has(key) && (
              <div className="border-t border-neutral-100">
                {rows.map((row) => {
                  const Icon = getRowIcon(row)
                  const isLangueRow = row.category.toLowerCase().includes('langue')
                  const isScannerRow = row.category.toLowerCase().includes('scanner')
                  const isActionRow = !!row.actionId
                  const langue = isLangueRow ? getLangueForRow(row) : null
                  const progColors = getProgrammeRowLabelColors(row)

                  return (
                    <div
                      key={row.id}
                      className="flex border-b border-neutral-50 last:border-b-0"
                    >
                      {/* Row label */}
                      <div
                        className="flex w-48 shrink-0 items-center gap-2.5 border-r border-neutral-100 px-4 py-3"
                        style={{ backgroundColor: progColors ? progColors.bg : row.backgroundColor + '60' }}
                      >
                        <div
                          className="flex h-7 w-7 items-center justify-center rounded-lg"
                          style={{ backgroundColor: progColors ? progColors.badge : row.backgroundColor }}
                        >
                          {Icon && <Icon className="h-3.5 w-3.5 text-neutral-600" />}
                        </div>
                        <span className="text-xs font-semibold text-neutral-700">
                          {row.title}
                        </span>
                        {row.dureeMinutes != null && (
                          <span className="ml-auto flex items-center gap-0.5 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[9px] font-medium text-neutral-500">
                            <Clock className="h-2.5 w-2.5" />
                            {row.dureeMinutes}min
                          </span>
                        )}
                        {isLangueRow && langue && (
                          <button
                            onClick={() =>
                              updateLangueStart.mutate({
                                etudiantMatiereId: langue.etudiantMatiereId,
                                debutLundi: !langue.debutLundi,
                              })
                            }
                            className="ml-auto rounded-md border border-neutral-200 bg-white p-1 text-neutral-400 shadow-sm transition-colors hover:text-neutral-600"
                            title={
                              langue.debutLundi
                                ? 'Décaler vers Mar-Ven'
                                : 'Décaler vers Lun-Jeu'
                            }
                          >
                            {langue.debutLundi ? (
                              <ChevronRight className="h-3 w-3" />
                            ) : (
                              <ChevronLeft className="h-3 w-3" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Day cells */}
                      <div className="flex flex-1 gap-1 p-1">
                        {WEEK_DAYS.map((day) => {
                          const dayIndex = WEEK_DAYS.indexOf(day)
                          const cell = getCell(row.id, day)
                          const cellKey = `${row.id}-${day}`
                          const isDragOver = dragOverCell === cellKey

                          return (
                            <div
                              key={day}
                              className={cn(
                                'flex flex-1 flex-col items-start gap-1 rounded-lg px-1.5 py-1.5 transition-colors',
                                isDragOver
                                  ? 'bg-primary/10 ring-1 ring-primary/30'
                                  : 'hover:bg-neutral-50',
                              )}
                              onDragOver={
                                isActionRow
                                  ? (e) => handleDragOver(e, cellKey, row)
                                  : undefined
                              }
                              onDragLeave={isActionRow ? handleDragLeave : undefined}
                              onDrop={
                                isActionRow
                                  ? (e) => handleDrop(e, day, row)
                                  : undefined
                              }
                            >
                              {/* Scanner row: always show icon */}
                              {isScannerRow && (
                                <div className="flex w-full justify-center py-1">
                                  <Eye className="h-4 w-4 text-amber-600/30" />
                                </div>
                              )}

                              {/* Langue row: vocabulary labels */}
                              {isLangueRow && langue && (
                                <LangueCell
                                  langue={langue}
                                  dayIndex={dayIndex}
                                  nbMots={nbMots}
                                  t={t}
                                />
                              )}

                              {/* Programme planning chips (action rows) */}
                              {!isScannerRow &&
                                !isLangueRow &&
                                cell?.programmePlannings?.slice().sort((a, b) => (a.matiereNom ?? '').localeCompare(b.matiereNom ?? '')).map((pp) => (
                                  <MatiereChip
                                    key={pp.id}
                                    item={pp}
                                    draggable={isActionRow && pp.actif}
                                    onDeactivate={
                                      isActionRow && pp.actif
                                        ? () => deactivatePlanning.mutate(pp.id)
                                        : undefined
                                    }
                                    onReactivate={
                                      isActionRow && !pp.actif
                                        ? () => reactivatePlanning.mutate(pp.id)
                                        : undefined
                                    }
                                    onDragStart={(e) => handleDragStart(e, pp, row)}
                                    onDragEnd={handleDragEnd}
                                  />
                                ))}

                              {/* Activity items */}
                              {!isScannerRow &&
                                !isLangueRow &&
                                cell?.activities?.map((act) => (
                                  <ActivityItem
                                    key={act.id}
                                    activity={act}
                                    rowCategory={row.category}
                                  />
                                ))}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-neutral-100 bg-neutral-50/50 px-4 py-2.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
          {t('student.planning.legend', 'Légende')} :
        </span>
        <div className="flex items-center gap-1.5">
          <span className="inline-flex h-4 cursor-grab items-center rounded-full border border-pink-300 bg-pink-50 px-2 text-[9px] font-semibold text-pink-600">
            <GripVertical className="mr-0.5 h-2 w-2 opacity-40" />
            Matière
          </span>
          <span className="text-[10px] text-neutral-500">= glisser pour déplacer</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-flex h-4 items-center rounded-full border border-neutral-300 bg-neutral-100 px-2 text-[9px] text-neutral-400 opacity-60">
            Désactivé
          </span>
          <span className="text-[10px] text-neutral-500">= cliquer pour réactiver</span>
        </div>
      </div>
    </div>
  )
}
